import React, { ChangeEvent, useState } from "react"
import styled from "styled-components"
import { dialCodes } from "../../../constants/countries"
import Input from "../../../ui/components/input/input.component"
import Select from "../../common/commerce/select/select.component"
import { ErrorMessage } from "../../common/general-styles.component"
import { noop } from "lodash"
import { AppFunctionComponent } from "../../../types"

const InputWrapper = styled.div`
  grid-area: InputWrapper;
`

const CodeWrapper = styled.div`
  grid-area: CodeWrapper;
`

const Error = styled(ErrorMessage)`
  grid-area: ErrorWrapper;
`

const FieldsetWrapper = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  grid-template-rows: 1fr auto;
  grid-template-areas: "CodeWrapper InputWrapper" "ErrorWrapper ErrorWrapper";
  grid-column-gap: 10px;
`

const Fieldset = styled.fieldset`
  border: none;
  padding: 0;
  display: contents;
  z-index: 1;

  div {
    margin: 0;
  }
`

export interface PhoneNumberInputProps {
  phoneNumber: string
  onChange?: (newValue: string) => void
  onBlur?: (newValue: string) => void
  error?: string
  name?: string
  className?: string
}

const findDialCode = (phone: string): string => {
  if (!phone) {
    return ""
  }
  const dialCodeItem = dialCodes.find((code) => phone.startsWith(code.value))
  return dialCodeItem ? dialCodeItem.value : ""
}

const getNumberWithoutDialCode = (phone: string, dialCode: string) =>
  phone ? phone.replace(dialCode, "").trim() : ""

const PhoneNumberInput: AppFunctionComponent<PhoneNumberInputProps> = ({
  className,
  phoneNumber,
  onChange = noop,
  onBlur = noop,
  error,
  name,
}) => {
  const dialCode = findDialCode(phoneNumber)
  const phoneNumberValue = getNumberWithoutDialCode(phoneNumber, dialCode)

  const [focused, setFocus] = useState(false)
  const focus = () => setFocus(true)
  const blur = () => setFocus(false)

  const onPhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(
      dialCode ? `${dialCode} ${event.target.value}` : event.target.value
    )

  const onPhoneNumberBlur = (event: ChangeEvent<HTMLInputElement>) => {
    onBlur(dialCode ? `${dialCode} ${event.target.value}` : event.target.value)
    blur()
  }

  const onDialCodeChange = (event: ChangeEvent<HTMLSelectElement>) =>
    onChange(`${event.target.value} ${phoneNumberValue}`)

  return (
    <FieldsetWrapper>
      {/* This wrapper is needed for proper grid usage. Related to: https://stackoverflow.com/questions/51076747/grid-layout-on-fieldset-bug-on-chrome */}
      <Fieldset data-testid="form-phone" className={className}>
        {/* <p> tags are necessary inside <fieldset> for Netlify forms to work properly as a single input */}
        <CodeWrapper>
          <Select
            placeholder={"Code"}
            options={dialCodes}
            value={dialCode}
            onFocus={focus}
            onChange={onDialCodeChange}
            onBlur={blur}
            data-testid={"dial-codes-select"}
            error={dialCode || focused ? undefined : error}
            showValues
            name={name}
            required
          />
        </CodeWrapper>
        <InputWrapper>
          <Input
            type={"text"}
            placeholder={"Phone"}
            value={phoneNumberValue}
            onChange={onPhoneNumberChange}
            onBlur={onPhoneNumberBlur}
            onFocus={focus}
            name={name}
            error={(!phoneNumberValue && error) || (dialCode && error)}
            required
          />
        </InputWrapper>
        {!focused && error && <Error>{error}</Error>}
      </Fieldset>
    </FieldsetWrapper>
  )
}

export default PhoneNumberInput
